import { apiUrl } from "../../../constants";

export const CHATBOT_REQUEST_TIMEOUT_MS = 90000;
export const CHATBOT_MAX_MESSAGE_LENGTH = 4000;
export const CHATBOT_MAX_TRANSCRIPT_ITEMS = 20;

function normalizeLine(line) {
  if (typeof line !== "string") {
    return "";
  }

  const trimmed = line.trim();
  const lineMatch = trimmed.match(/^line\s*(\d+)$/i);

  if (lineMatch) {
    return `Line${lineMatch[1]}`;
  }

  return trimmed;
}

function normalizeTranscript(transcript = []) {
  return transcript
    .filter(
      (turn) =>
        (turn?.role === "user" || turn?.role === "assistant") &&
        typeof turn?.content === "string" &&
        turn.content.trim()
    )
    .map((turn) => ({
      role: turn.role,
      content: turn.content.trim(),
    }))
    .slice(-CHATBOT_MAX_TRANSCRIPT_ITEMS);
}

function normalizePageContext(pageContext = {}) {
  const parsedFactory = Number(pageContext?.factory);

  return {
    plant_id: pageContext?.plant_id || "",
    factory: Number.isNaN(parsedFactory) ? 0 : parsedFactory,
    line: normalizeLine(pageContext?.line),
    page: pageContext?.page || "",
  };
}

export function buildChatbotPayload({ message, transcript = [], pageContext = {} }) {
  const normalizedMessage = String(message || "").trim();

  return {
    message: normalizedMessage,
    transcript: normalizeTranscript(transcript),
    page_context: normalizePageContext(pageContext),
  };
}

export function validateChatbotPayload(payload) {
  if (!payload?.message) {
    return "กรุณาพิมพ์ข้อความก่อนส่ง";
  }

  if (payload.message.length > CHATBOT_MAX_MESSAGE_LENGTH) {
    return `ข้อความยาวเกิน ${CHATBOT_MAX_MESSAGE_LENGTH} ตัวอักษร`;
  }

  if (Array.isArray(payload?.transcript) && payload.transcript.length > CHATBOT_MAX_TRANSCRIPT_ITEMS) {
    return `ประวัติสนทนายาวเกิน ${CHATBOT_MAX_TRANSCRIPT_ITEMS} รายการ`;
  }

  return "";
}

function createChatbotRequestError({
  status = 0,
  code = "chatbot_unknown_error",
  detail = "",
  upstreamStatus = null,
}) {
  const error = new Error(detail || code || "chatbot_unknown_error");
  error.name = "ChatbotRequestError";
  error.code = code;
  error.status = status;
  error.upstream_status = upstreamStatus;
  error.response = {
    status,
    data: {
      code,
      detail,
      upstream_status: upstreamStatus,
      metadata: upstreamStatus !== null ? { upstream_status: upstreamStatus } : undefined,
    },
  };
  return error;
}

function mergeAbortSignals(signalA, signalB) {
  if (!signalA && !signalB) {
    return { signal: undefined, cleanup: () => {} };
  }

  if (!signalA) {
    return { signal: signalB, cleanup: () => {} };
  }

  if (!signalB) {
    return { signal: signalA, cleanup: () => {} };
  }

  const controller = new AbortController();

  const abort = () => {
    if (!controller.signal.aborted) {
      controller.abort();
    }
  };

  signalA.addEventListener("abort", abort);
  signalB.addEventListener("abort", abort);

  if (signalA.aborted || signalB.aborted) {
    abort();
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      signalA.removeEventListener("abort", abort);
      signalB.removeEventListener("abort", abort);
    },
  };
}

async function parseErrorResponse(response) {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    try {
      const json = await response.json();
      return {
        code: json?.code || `http_${response.status}`,
        detail: json?.detail || "",
        upstreamStatus: json?.upstream_status || null,
      };
    } catch {
      return {
        code: `http_${response.status}`,
        detail: "",
        upstreamStatus: null,
      };
    }
  }

  return {
    code: `http_${response.status}`,
    detail: "",
    upstreamStatus: null,
  };
}

async function parseJsonFallbackResponse(response) {
  try {
    const json = await response.json();

    return {
      code: json?.code || `http_${response.status}`,
      detail: json?.detail || "",
      upstreamStatus: json?.upstream_status || null,
    };
  } catch {
    return {
      code: `http_${response.status}`,
      detail: "",
      upstreamStatus: null,
    };
  }
}

function parseSseFrame(frame) {
  const lines = frame.split(/\r?\n/);
  let event = "message";
  const data = [];

  for (const line of lines) {
    if (!line || line.startsWith(":")) {
      continue;
    }

    if (line.startsWith("event:")) {
      event = line.slice(6).trim() || "message";
      continue;
    }

    if (line.startsWith("data:")) {
      data.push(line.slice(5).trimStart());
    }
  }

  return {
    event,
    data: data.join("\n"),
  };
}

export async function sendChatbotMessageStream({
  message,
  transcript,
  pageContext,
  token,
  signal,
  onEvent,
}) {
  const payload = buildChatbotPayload({ message, transcript, pageContext });
  const validationMessage = validateChatbotPayload(payload);

  if (validationMessage) {
    throw createChatbotRequestError({
      code: "chatbot_invalid_request",
      detail: validationMessage,
      status: 400,
    });
  }

  const timeoutController = new AbortController();
  const timeoutId = window.setTimeout(() => {
    timeoutController.abort();
  }, CHATBOT_REQUEST_TIMEOUT_MS);

  const { signal: mergedSignal, cleanup } = mergeAbortSignals(signal, timeoutController.signal);

  try {
    const requestStream = async () => {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      };

      return fetch(apiUrl.CHATBOT_CHAT_STREAM, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
        signal: mergedSignal,
      });
    };

    const response = await requestStream();

    if (!response.ok) {
      const errorData = await parseErrorResponse(response);
      throw createChatbotRequestError({
        status: response.status,
        code: errorData.code,
        detail: errorData.detail,
        upstreamStatus: errorData.upstreamStatus,
      });
    }

    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json") && !contentType.includes("text/event-stream")) {
      const errorData = await parseJsonFallbackResponse(response);
      throw createChatbotRequestError({
        status: response.status,
        code: errorData.code,
        detail: errorData.detail,
        upstreamStatus: errorData.upstreamStatus,
      });
    }

    if (!response.body) {
      throw createChatbotRequestError({
        status: 502,
        code: "chatbot_invalid_response",
        detail: "ไม่พบข้อมูล stream จากระบบ",
      });
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        if (buffer.trim()) {
          onEvent?.(parseSseFrame(buffer));
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split(/\r?\n\r?\n/);
      buffer = frames.pop() || "";

      for (const frame of frames) {
        const parsedFrame = parseSseFrame(frame);
        if (parsedFrame.data || parsedFrame.event) {
          if (onEvent?.(parsedFrame) === true) {
            // Logical completion must not wait for the server to close HTTP.
            void reader.cancel().catch(() => {});
            return;
          }
        }
      }
    }
  } catch (error) {
    if (error?.name === "AbortError") {
      const timedOut = timeoutController.signal.aborted && !signal?.aborted;

      if (timedOut) {
        throw createChatbotRequestError({
          status: 504,
          code: "chatbot_timeout",
          detail: "ระบบตอบช้า กรุณาลองใหม่",
        });
      }
    }

    throw error;
  } finally {
    window.clearTimeout(timeoutId);
    cleanup();
  }
}
